import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, ChevronRight, Check, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useRole } from "@/lib/roles";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { roleRecHeader } from "@/lib/role-content";
import { EntityKindFilter } from "@/components/control-tower/EntityKindFilter";
import { FilterChipRow } from "@/components/control-tower/FilterChipRow";
import { ENTITY_KIND_FILTER_LABEL } from "@/components/building/findings/findings-helpers";
import { entityKindForStrategyField, type EntityKind } from "@/lib/entity-kind";
import { shouldAutoOpenTop } from "@/components/control-tower/recommendation-feed-logic";
import {
  listStrategyProposalsForScope,
  acceptStrategyProposal,
  rejectStrategyProposal,
  type StrategyProposal,
  type Building,
} from "@/lib/api/building";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { encodeInventoryScope } from "@/lib/inventory-scope-codec";
import { STRATEGY_FIELD_LABELS } from "@/components/building/optimization/strategy-fields";

interface ScopedProposal {
  proposal: StrategyProposal;
  building: Building;
}

export function RecommendationFeed({
  buildings,
  onProposalsUpdated,
}: {
  buildings: Building[];
  /** After a successful accept/reject — Säästöpotentiaali refreshes Optimointistrategia. */
  onProposalsUpdated?: () => void;
}) {
  const [items, setItems] = useState<ScopedProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [entityKind, setEntityKind] = useState<EntityKind | "all">("all");
  // Tracks whether the operator has picked a row themselves, so the auto-opened "top" row (below)
  // stops re-selecting itself out from under them as later buildings settle in.
  const userOpenedRef = useRef(false);
  // Tracks whether auto-open has already fired this load cycle, so it doesn't re-fire (and yank
  // the open panel) on every progressive settle — see shouldAutoOpenTop.
  const autoOpenedRef = useRef(false);
  const { role } = useRole();
  const header = roleRecHeader[role];
  const { filter, groupId, buildingId } = useInventoryFilter();
  const scope = encodeInventoryScope({ filter, groupId, buildingId });
  const scopeKey = `${scope.building ?? ""}|${scope.group ?? ""}|${scope.city ?? ""}|${scope.type ?? ""}|${scope.status ?? ""}`;

  useEffect(() => {
    if (buildings.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setItems([]);
    setLoading(true);
    userOpenedRef.current = false;
    autoOpenedRef.current = false;
    const buildingById = new Map(buildings.map((b) => [b.id, b]));
    // Same query encoding as listFindingsForScope (#1251 / bug #1348) — never a building_ids list.
    listStrategyProposalsForScope(scope, "pending")
      .then((proposals) => {
        if (cancelled) return;
        const scoped = proposals
          .map((proposal): ScopedProposal | null => {
            const building = buildingById.get(proposal.building_id);
            return building ? { proposal, building } : null;
          })
          .filter((row): row is ScopedProposal => row !== null)
          .sort((a, b) =>
            b.proposal.created_at.localeCompare(a.proposal.created_at),
          );
        setItems(scoped);
        if (
          shouldAutoOpenTop(
            userOpenedRef.current,
            autoOpenedRef.current,
            scoped.length > 0,
          )
        ) {
          autoOpenedRef.current = true;
          setOpen(scoped[0].proposal.id);
        }
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // scopeKey is the stable primitive; `scope` is a fresh object every render
    // (encodeInventoryScope), same as useScopedFindings (#1251). `buildings` stays for
    // the name/Accept-Reject join — StrategyProposal has no building_name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, buildings]);

  async function handleAccept(item: ScopedProposal) {
    // Remove the item only on success; on failure keep it in place and surface a toast so the
    // action stays retryable (mirrors BuildingManagerOverlay).
    try {
      await acceptStrategyProposal(item.building.id, item.proposal.id);
      setItems((prev) =>
        prev.filter((i) => i.proposal.id !== item.proposal.id),
      );
      onProposalsUpdated?.();
    } catch {
      toast.error("Suosituksen hyväksyntä epäonnistui");
    }
  }

  async function handleReject(item: ScopedProposal) {
    try {
      await rejectStrategyProposal(item.building.id, item.proposal.id);
      setItems((prev) =>
        prev.filter((i) => i.proposal.id !== item.proposal.id),
      );
      onProposalsUpdated?.();
    } catch {
      toast.error("Suosituksen hylkäys epäonnistui");
    }
  }

  // Entity kind composes with — never replaces — the pending-status scope already applied above.
  const filteredItems = useMemo(
    () =>
      entityKind === "all"
        ? items
        : items.filter(
            (item) =>
              entityKindForStrategyField(item.proposal.field) === entityKind,
          ),
    [items, entityKind],
  );

  const subtitle = header.subtitle.replace(
    /\b\d+\b/,
    String(filteredItems.length),
  );

  return (
    <section className={cn(cardBase, "overflow-hidden")}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-primary shrink-0" />
          <h2 className="text-sm font-semibold tracking-tight">
            {header.title}
          </h2>
          <span className="text-[11px] text-muted-foreground truncate">
            {loading ? "Ladataan…" : subtitle}
          </span>
        </div>
        <button className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0">
          Avaa kaikki <ArrowUpRight className="h-3 w-3" />
        </button>
      </header>

      <div className="px-4 pt-2 pb-2 border-b border-border">
        <FilterChipRow label={ENTITY_KIND_FILTER_LABEL}>
          <EntityKindFilter active={entityKind} onChange={setEntityKind} />
        </FilterChipRow>
      </div>

      {loading && items.length === 0 ? (
        <ul className="divide-y divide-border">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="px-4 py-4">
              <div className="h-4 w-2/3 rounded bg-surface animate-pulse mb-2" />
              <div className="h-3 w-1/3 rounded bg-surface animate-pulse" />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="divide-y divide-border">
          {!loading && filteredItems.length === 0 && (
            <li className="px-4 py-8 text-center text-xs text-muted-foreground">
              Ei avoimia suosituksia.
            </li>
          )}
          {filteredItems.map((item) => {
            const { proposal, building } = item;
            const isOpen = open === proposal.id;
            const meta = STRATEGY_FIELD_LABELS[proposal.field];
            const label = meta.label;
            const unit = meta.unit;

            return (
              <li key={proposal.id} className="bg-card">
                <button
                  onClick={() => {
                    userOpenedRef.current = true;
                    setOpen(isOpen ? null : proposal.id);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-surface/60 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <ChevronRight
                      className={`h-4 w-4 mt-0.5 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-90 text-primary" : ""
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{label}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {building.name} · {building.city}
                      </div>
                    </div>
                    {proposal.current_value != null &&
                      proposal.proposed_value != null && (
                        <div className="text-right shrink-0 text-xs text-muted-foreground">
                          <span className="line-through mr-1">
                            {proposal.current_value} {unit}
                          </span>
                          <span className="text-primary font-medium">
                            {proposal.proposed_value} {unit}
                          </span>
                        </div>
                      )}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-12 pb-4 -mt-1 space-y-3 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">
                        Perustelu
                      </div>
                      <p className="text-foreground/90 leading-relaxed">
                        {proposal.justification_fi}
                      </p>
                    </div>

                    <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-border/60">
                      <button
                        onClick={() => handleReject(item)}
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium border border-border bg-transparent text-muted-foreground hover:text-foreground transition"
                      >
                        <X className="h-3 w-3" />
                        Hylkää
                      </button>
                      <button
                        onClick={() => handleAccept(item)}
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium bg-primary text-primary-foreground border border-primary hover:opacity-90 shadow-glow transition"
                      >
                        <Check className="h-3 w-3" />
                        Hyväksy
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
